import { useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth'
import { isSuperAdmin } from '@/lib/role'

const TENANT_PREFIX = /^\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i

/**
 * The tenant a URL is scoped to, or null on the unscoped (super-admin) routes.
 * Reading side of the same prefix `useTenantPath` writes.
 */
function tenantIdFromPath(pathname: string): string | null {
  return pathname.match(TENANT_PREFIX)?.[1] ?? null
}

/**
 * Keeps the session's active tenant in step with the URL — the URL is the single
 * source of truth for which tenant is being viewed.
 *
 * Call this ONCE, from the layout above the route-level Suspense boundary. A lazy
 * route chunk suspending hides the subtree below that boundary, which destroys and
 * re-runs the effects inside it; a writer living down there that wrote a different
 * value on teardown would fight itself forever (React #185, "Maximum update depth
 * exceeded"). Up here the effect survives the fallback, and because every write is
 * derived from the URL, a re-run writes the same value and can never loop.
 *
 * Layout effect, not passive: the scope must be set before paint so tenant pages
 * never flash the super-admin view or fire queries without the X-Tenant-ID header.
 */
export function useTenantScopeSync(): void {
  const { pathname } = useLocation()
  const user = useAuthStore((s) => s.user)
  const activeTenantId = useAuthStore((s) => s.activeTenantId)
  const setActiveTenantId = useAuthStore((s) => s.setActiveTenantId)
  const urlTenantId = tenantIdFromPath(pathname)

  useLayoutEffect(() => {
    if (urlTenantId) {
      if (activeTenantId !== urlTenantId) setActiveTenantId(urlTenantId)
      return
    }
    // Unscoped URL: only a super admin drops the tenant view. A tenant user has no
    // other scope to fall back to — ProtectedLayout redirects them to their prefix.
    if (isSuperAdmin(user?.role) && activeTenantId) {
      setActiveTenantId(null)
    }
  }, [urlTenantId, activeTenantId, user?.role, setActiveTenantId])
}

/**
 * Returns a function that prepends the active tenant ID to a path.
 * Use in tenant-scoped pages to ensure links preserve the tenant URL prefix.
 *
 * Usage:
 *   const tp = useTenantPath()
 *   navigate(tp('/dashboard'))
 *   <Link to={tp('/settings/users')}>
 */
export function useTenantPath() {
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)
  const activeTenantId = useAuthStore((s) => s.activeTenantId)
  const prefix = activeTenantId
    ? `/${activeTenantId}`
    : user?.role !== 'super_admin' && tenant?.id
      ? `/${tenant.id}`
      : ''
  return (path: string) => `${prefix}${path}`
}
